import React, { Component, useCallback } from "react";
import PropTypes from "prop-types";
import Spinner from "react-spinner";
import classNames from "classnames";
import debounce from "lodash/debounce";
import noop from "lodash/noop";
import uniqueId from "lodash/uniqueId";
import stringUtils from "../../utils/string";
import { matchNode } from "../../utils/dom";
import SearchField from "./SearchField";
import SearchResult from "./SearchResult";

// SASS Imports
require("./SearchSelectList.scss");

const LOAD_MORE_THRESHOLD = 30;
const MIN_SEARCH_CHARS = 2;

export default class SearchSelectList extends Component {
  constructor (props) {
    super(props);
    this.state = {
      total: 0,
      currentPage: 0,
      searchValue: "",
      searchFilter: "",
      showAll: false,
      filterOptions: [],
      results: [],
      loading: false,
      hasMore: false,
      resultsListHeight: 0
    };
    this._currentRequestId = null;
    this.onChangeSearch = this.onChangeSearch.bind(this);
    this.onChangeFilter = this.onChangeFilter.bind(this);
    this.onFetch = debounce(this.onFetch.bind(this), 1);
    this.handleScroll = debounce(this.handleScroll.bind(this), 20);
    this.showAll = this.showAll.bind(this);
  }

  componentDidMount() {
    this.refs.selectListResults.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    this.refs.selectListResults.removeEventListener("scroll", this.handleScroll);
  }

  componentDidUpdate(prevProps, prevState) {
    const currentSearchValue = this.state.searchValue.trim();

    const searchValueChanged = prevState.searchValue.trim() !== currentSearchValue;
    const searchFilterChanged = prevState.searchFilter !== this.state.searchFilter;

    if (searchValueChanged || searchFilterChanged) {
      this.reset({ clearFilterOptions: searchValueChanged });

      if (currentSearchValue.length >= MIN_SEARCH_CHARS) {
        this.onFetch();
      }
    }
  }

  showAll() {
    this.onFetch(true);
  }

  async onFetch(showAll = false) {
    const _requestId = uniqueId("search-");

    const searchValue = !showAll ? this.state.searchValue.trim() : "";
    const searchFilter = !showAll ? this.state.searchFilter : "";
    const size = this.props.perPage;
    const from = this.state.currentPage * size;

    this._currentRequestId = _requestId;

    this.setState({ loading: true, showAll });

    const data = await this.props.onFetch(searchValue, searchFilter, size, from);

    // Request should be ignored, another has come in since
    if (_requestId !== this._currentRequestId) {
      return;
    }

    const results = [...this.state.results, ...data.results];

    const filterOptions = this.state.searchFilter.length < 1 ? data.filterOptions : this.state.filterOptions;

    const currentPage = this.state.currentPage + 1;

    this.setState({
      results,
      filterOptions,
      total: data.total,
      hasMore: data.total > (from + size),
      loading: false,
      currentPage
    });
  }

  reset({ clearFilterOptions = false } = {}) {
    this._currentRequestId = null;

    if (this.props.onReset) {
      this.props.onReset();
    }

    const newState = {
      currentPage: 0,
      total: 0,
      results: [],
    };

    if (clearFilterOptions) {
      newState.filterOptions = [];
    };

    this.setState(newState);
  }

  handleScroll(e) {
    if (this.state.loading || !this.state.hasMore) {
      return;
    }

    const containerHeight = this.refs.selectList.clientHeight - this.refs.selectListHeader.clientHeight;

    const fetchMoreHeight = containerHeight + this.refs.selectListResults.scrollTop + LOAD_MORE_THRESHOLD;

    if (this.refs.selectListResults.scrollHeight <= fetchMoreHeight) {
      this.onFetch();
    }
  }

  onChangeSearch(value) {
    this.setState({ searchValue: value, searchFilter: "" });
  }

  onChangeFilter(value) {
    this.setState({ searchFilter: value });
  }

  render() {
    const showingStaticList = this.props.showStaticList && (this.state.searchValue.length === 0 && !this.state.showAll) && (this.props.staticListResults != null);

    const total = showingStaticList ? this.props.staticListResults.length : this.state.total;

    return (
      <div ref="selectList" className={classNames("searchSelectList", { hidden: this.props.isHidden })}>
        <div ref="selectListHeader" className="searchSelectListHeader">
          <div className="container-fluid">
            <SearchField
              searchValue={this.state.searchValue}
              searchFilter={this.state.searchFilter}
              searchPlaceholder={this.props.searchPlaceholder}
              filterPlaceholder={this.props.filterPlaceholder}
              onChangeSearch={this.onChangeSearch}
              onChangeFilter={this.onChangeFilter}
              filterOptions={this.state.filterOptions}
              showFilter={this.props.showFilter}
            />
            <div className="row listLabel">
              <div className="col-xs-12" />
            </div>
          </div>
        </div>

        <div ref="selectListResults" className="searchListResultWrapper">
          <div className="container-fluid">
            <Status showAll={this.showAll} showAllLink={this.props.showAllLink} total={total} />
            <StaticHeader showingStaticList={showingStaticList} text={this.props.staticListHeader} />
            <Loader loading={this.state.loading} />
            <SearchResult
              isShowingStatic={showingStaticList}
              results={showingStaticList ? this.props.staticListResults : this.state.results}
              renderItem={this.props.renderItem}
              onBeforeItem={this.props.onBeforeItem}
              itemProps={this.props.itemProps}
              searchValue={this.state.searchValue}
            />
          </div>
        </div>
      </div>
    );
  }
}

SearchSelectList.displayName = "SelectLists.SearchSelectList";

SearchSelectList.propTypes = {
  onFetch: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  title: PropTypes.string,
  showStaticList: PropTypes.bool,
  staticListHeader: PropTypes.string,
  staticListResults: PropTypes.array,
  searchPlaceholder: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  showAllLink: PropTypes.string,
  showFilter: PropTypes.bool,
  isHidden: PropTypes.bool
};

SearchSelectList.defaultProps = {
  title: "",
  isHidden: false,
  showStaticList: true,
  staticListHeader: "My Favorites",
  staticListResults: [],
  searchPlaceholder: "Search",
  filterPlaceholder: "Filter",
  onBeforeItem: false,
  showFilter: true,
  perPage: 10,
};

function Loader({ loading }) {
  if (loading) {
    return (
      <div className="searchSpinner">
        <Spinner />
      </div>
    );
  }

  return null;
}

Loader.displayName = "SelectLists.SearchSelectList.Loader";

Loader.propTypes = {
  loading: PropTypes.bool.isRequired,
};

Loader.defaultProps = {
  loading: false
};

function StaticHeader({ showingStaticList, text }) {
  if (showingStaticList) {
    return (
      <div className="row listLabel">
        <div className="col-xs-12">
          <h6>{text}</h6>
        </div>
      </div>
    );
  }

  return null;
}

StaticHeader.displayName = "SelectLists.SearchSelectList.StaticHeader";

StaticHeader.propTypes = {
  showingStaticList: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

StaticHeader.defaultProps = {
  showingStaticList: false,
  text: "My Favorites",
};

function Status({ showAll, showAllLink, total }) {
  return (
    <div className="row numberOfResults">
      <div className="col-xs-12">
        <h6>
          Showing <span className="blueText">{total}</span>
          {(total === 1) ? " result" : " results"}
          <ShowAllLink showAll={showAll} showAllLink={showAllLink} />
        </h6>
      </div>
    </div>
  );
}

Status.displayName = "SelectLists.SearchSelectList.Status";

Status.propTypes = {
  showAll: PropTypes.func.isRequired,
  showAllLink: PropTypes.string,
  total: PropTypes.number.isRequired,
};

Status.defaultProps = {
  showAll: noop,
  showAllLink: null,
  total: 0,
};

function ShowAllLink({ showAll, showAllLink }) {
  const onShowAll = useCallback(() => showAll(), [showAll]);

  if (showAllLink != null) {
    return (
      <span>
        {" "}
        (<a onClick={onShowAll}>{showAllLink}</a>)
      </span>
    )
  }

  return null;
}

ShowAllLink.displayName = "SelectLists.SearchSelectList.ShowAllLink";

ShowAllLink.propTypes = {
  showAll: PropTypes.func.isRequired,
  showAllLink: PropTypes.string,
};

ShowAllLink.defaultProps = {
  showAll: noop,
  showAllLink: null,
};



// WEBPACK FOOTER //
// ./app/components/SelectLists/SearchSelectList.jsx